import { useState, useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Users, Filter, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, type Paginated, type Ticket as TicketType } from "../lib/api";
import { GlassCard } from "../components/ui/GlassCard";
import { PriorityBadge } from "../components/tickets/PriorityBadge";
import { StatusBadge } from "../components/tickets/StatusBadge";
import { CategoryBadge } from "../components/tickets/CategoryBadge";
import { SLACountdown } from "../components/tickets/SLACountdown";
import { Avatar } from "../components/ui/Avatar";
import { EmptyState } from "../components/ui/EmptyState";
import { fmtRelative, truncate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { TicketDetailModal } from "../components/tickets/TicketDetailModal";

export default function DepartmentTickets() {
  const { user } = useAuth();
  const [search, setSearch] = useSearchParams();
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  const page = parseInt(search.get("page") || "1", 10);

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(search);
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    setSearch(next, { replace: true });
  };

  const params = useMemo(() => {
    const p: Record<string, unknown> = { page, pageSize: 20 };
    // Pass department_view flag so the backend knows to fetch tickets for the user's department
    if (user?.department) {
      p.department_view = "true";
    }
    return p;
  }, [page, user?.department]);

  const { data, isLoading } = useQuery({
    queryKey: ["tickets.department", params],
    queryFn: async () =>
      (await api.get<Paginated<TicketType>>("/api/tickets", { params })).data,
    placeholderData: keepPreviousData,
  });

  // Filter client-side just in case the backend ignores the department param
  const items = useMemo(() => {
    if (!data?.items) return [];
    if (!user?.department) return data.items;
    
    // Check if the backend filtered it or if we need to filter locally
    const filtered = data.items.filter(
      (t) => t.submitter?.department?.toLowerCase() === user.department?.toLowerCase()
    );
    // If backend already filtered, items.length will likely match filtered.length
    // But we strictly return filtered to ensure they only see their department.
    return filtered;
  }, [data?.items, user?.department]);

  const totalPages = data?.totalPages || 1;

  if (!user?.department) {
    return (
      <GlassCard>
        <EmptyState
          icon={Users}
          title="No Department Assigned"
          description="You do not have a department assigned to your profile, so you cannot view department tickets."
          action={
            <Link to="/dashboard" className="btn-primary inline-flex">
              Back to Dashboard
            </Link>
          }
        />
      </GlassCard>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Users className="w-6 h-6 text-brand-300" />
            {user.department} Tickets
          </h1>
          <p className="page-subtitle">
            View tickets submitted by others in your department.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="skeleton h-16" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={Filter}
            title="No tickets found"
            description={`There are currently no tickets from users in the ${user.department} department.`}
          />
        </GlassCard>
      ) : (
        <GlassCard padded={false}>
          <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-3 bg-white/[0.02] border-b border-white/5 text-[10px] uppercase tracking-wider text-slate-500 font-bold select-none">
            <div className="col-span-2">Ticket ID</div>
            <div className="col-span-4">Ticket Details</div>
            <div className="col-span-2">Priority & Category</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2">Submitter</div>
            <div className="col-span-1 text-right">SLA</div>
          </div>
          <div className="divide-y divide-white/5">
            {items.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTicketId(t.id)}
                className="w-full text-left grid grid-cols-12 gap-3 items-center px-4 py-3.5 hover:bg-white/[0.03] transition-colors"
              >
                <div className="col-span-12 lg:col-span-2 flex items-center">
                  <span className="font-mono text-xs text-slate-400 bg-white/5 px-2 py-1 rounded border border-white/10">
                    {t.id.slice(-8).toUpperCase()}
                  </span>
                </div>
                <div className="col-span-12 lg:col-span-4 min-w-0">
                  <div className="text-sm text-white font-medium truncate">
                    {t.title}
                  </div>
                  <div className="text-xs text-slate-400 truncate mt-0.5">
                    {truncate(t.description, 120)}
                  </div>
                </div>
                <div className="col-span-6 lg:col-span-2 flex items-center gap-1.5 flex-wrap">
                  <PriorityBadge priority={t.priority} />
                  <CategoryBadge category={t.category} />
                </div>
                <div className="col-span-3 lg:col-span-1">
                  <StatusBadge status={t.status} />
                </div>
                <div className="col-span-3 lg:col-span-2 flex items-center text-xs text-slate-400">
                  {t.submitter ? (
                    <div className="flex items-center gap-1.5" title={t.submitter.name}>
                      <Avatar name={t.submitter.name} url={t.submitter.avatarUrl} size="xs" />
                      <span className="truncate">{t.submitter.name}</span>
                    </div>
                  ) : (
                    <span className="italic text-slate-500">Unknown</span>
                  )}
                </div>
                <div className="col-span-12 lg:col-span-1 flex lg:justify-end">
                  <SLACountdown
                    deadline={t.slaDeadline}
                    priority={t.priority}
                    status={t.status}
                    compact
                  />
                </div>
                <div className="col-span-12 lg:col-span-12 flex items-center text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-1 lg:mt-0">
                  {fmtRelative(t.createdAt)}
                </div>
              </button>
            ))}
          </div>
        </GlassCard>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            disabled={page <= 1}
            onClick={() => updateParam("page", String(page - 1))}
            className="btn-ghost disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <span className="text-sm text-slate-300 px-3">
            Page <b className="text-white">{page}</b> of {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => updateParam("page", String(page + 1))}
            className="btn-ghost disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <TicketDetailModal
        ticketId={selectedTicketId}
        open={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
      />
    </div>
  );
}
